import { type FunctionComponent, Suspense, lazy, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, ScrollRestoration, useLocation, useNavigate } from 'react-router';
import { DatadogErrorBoundary } from '@my-medication/shared-components';
import { useJourneyTracking } from '@my-medication/shared-components/hooks/useJourneyTracking';
import { useRumViewNames } from '@my-medication/shared-components/hooks/useRumViewNames';
import { EventBusProvider } from '@my-medication/shared-components/providers/EventBusProvider';
import { ReactRouterAdapter } from '@my-medication/shared-components/providers/ReactRouterAdapter';
import { TranslationProvider } from '@my-medication/shared-components/providers/TranslationProvider';
import { AdminSessionInvalidDialog } from '@components/dialogs/AdminSessionInvalidDialog';
import { AdminApiProvider } from '@components/providers/AdminApiProvider';
import { AdminAppInitializer } from '@components/providers/AdminAppInitializer';
import { setAdminUnauthorizedHandler } from '@hooks/useAdminBffApi';
import { useAdminStore } from '@store/adminStore';

import { AppShell, Footer } from '@components';

import { loadLocaleNamespace } from '@i18n/loadLocaleNamespace';
import { AdminPortalKeys } from '@i18n/translations';
import { AppHydrateFallback } from '@router/AppHydrateFallback';
import { routes } from '@router/routes';
import { analyticsService } from '@services/googleTagManagerService';
import { fontSize as fontSizeService, type FontSizeValue } from '@services/fontSize';
import { journeys } from '@services/operations/journeys';
import { remoteApi } from '@services/remoteApi';
import { userStateBridge } from '@services/userStateBridge';

const AVAILABLE_LOCALES = ['de', 'fr', 'it'];
const DEFAULT_LOCALE = 'de';

const LOGOUT_LABEL_KEY = AdminPortalKeys.Navigation_Logout;
const MENU_LABEL_KEY = AdminPortalKeys.Navigation_Menu;
const LOGO_CLAIM_LABEL_KEY = AdminPortalKeys.Header_Claim;

const currentLocaleFromPath = (pathname: string): string => {
  const [, first] = pathname.split('/');
  return AVAILABLE_LOCALES.includes(first) ? first : DEFAULT_LOCALE;
};

const routeTree = [{ children: routes }];

const Snackbar = lazy(() => import('@my-medication/shared-components/Snackbar').then((m) => ({ default: m.Snackbar })));

const AdminShellContent: FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const roles = useAdminStore((state) => state.user.roles);
  const fontSize = useAdminStore((state) => state.user.fontSize);

  useEffect(() => {
    remoteApi.setNavigate((to: string) => navigate(to));
  }, [navigate]);

  useEffect(() => {
    setAdminUnauthorizedHandler(() => {
      if (userStateBridge.isSessionReady() && userStateBridge.isAuthenticated())
        userStateBridge.setSessionInvalid(true);
    });

    return () => setAdminUnauthorizedHandler(undefined);
  }, []);

  useEffect(() => {
    fontSizeService.restore();
  }, []);

  useRumViewNames();
  useJourneyTracking(journeys);

  const handleFontSizeChange = useCallback((size: FontSizeValue) => fontSizeService.set(size), []);

  const handleLogout = useCallback(() => {
    void remoteApi.logout();
  }, []);

  return (
    <div className="flex min-h-screen flex-col justify-between">
      <AppShell
        currentRoles={roles ?? []}
        fontSize={fontSize ?? 'normal'}
        onFontSizeChange={handleFontSizeChange}
        onLogout={handleLogout}
        logoutLabel={t(LOGOUT_LABEL_KEY)}
        menuLabel={t(MENU_LABEL_KEY)}
        logoClaimLabel={t(LOGO_CLAIM_LABEL_KEY)}
      />

      <main id="main" className="centered-card-layout-page grow-[999] bg-secondary-100 pt-4!">
        <Outlet />
      </main>

      <Footer />
      <ScrollRestoration />
    </div>
  );
};

const App: FunctionComponent = () => {
  const location = useLocation();
  const locale = currentLocaleFromPath(location.pathname);

  useEffect(() => {
    useAdminStore.getState().setLocale(locale);
  }, [locale]);

  useEffect(() => {
    analyticsService.initialize();
  }, []);

  return (
    <TranslationProvider
      lng={locale}
      fallbackLng={DEFAULT_LOCALE}
      loadAppNamespace={loadLocaleNamespace}
      fallback={<AppHydrateFallback />}
    >
      <AdminApiProvider server={remoteApi}>
        <ReactRouterAdapter routes={routeTree}>
          <AdminAppInitializer>
            <EventBusProvider>
              <AdminShellContent />
              <AdminSessionInvalidDialog />
              <DatadogErrorBoundary context={{ boundary: 'Snackbar' }}>
                <Suspense fallback={null}>
                  <Snackbar />
                </Suspense>
              </DatadogErrorBoundary>
            </EventBusProvider>
          </AdminAppInitializer>
        </ReactRouterAdapter>
      </AdminApiProvider>
    </TranslationProvider>
  );
};

export { App };
